import { FaroInsightsWrapper } from "@/grafana-faro"
import { ConsultationReportDrawer } from "@/modules/patients/detail/consultation-report/consultation-report-drawer"
import { useConsultationReportStoreContext } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { ProgressNoteData } from "@/modules/patients/detail/progress-note/types/progress-note-fragment-type"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery"
import { useConfirmStore } from "@/stores/useConfirmStore"
import { useDrawerStore } from "@/stores/useDrawerStore"

const rejectedAppointmentIds = new Set<string>()

const hasProgressNoteMissingConsultationReport = (progressNote: ProgressNoteData): boolean => {
  if (progressNote.__typename === "ch_ProgressNote") {
    return !progressNote.ch_consultationReport
  }
  return false
}

const useMissingConsultationReportCheck = (editableProgressNotes: readonly ProgressNoteData[]) => {
  const appointmentId = useAppointmentParam()
  const country = useCountryParam()
  const patientId = usePatientParam()
  const contextAppointment = useContextAppointment()
  const confirm = useConfirmStore((state) => state.confirm)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const setConsultationReportRef = useConsultationReportStoreContext((state) => state.setConsultationReportRef)

  const [patientData] = usePatientByIdQuery(patientId, country, appointmentId)
  const patient = patientData.patientById

  const hasNonMedgateInDoctorNetwork = patient?.__typename === "ch_Patient" && patient.ch_hasNonMedgateInDoctorNetwork

  const isMedicalConsultation = (): boolean => {
    return !!contextAppointment && contextAppointment.service !== "ADMINISTRATIVE_REQUEST"
  }

  const getFirstProgressNoteMissingConsultationReport = (): ProgressNoteData | undefined => {
    return editableProgressNotes.find((pn) => hasProgressNoteMissingConsultationReport(pn))
  }

  const checkMissingConsultationReport = async (onProceedToConclude: () => void) => {
    if (!isMedicalConsultation() || !hasNonMedgateInDoctorNetwork) {
      onProceedToConclude()
      return
    }

    const firstMissingProgressNote = getFirstProgressNoteMissingConsultationReport()
    if (!firstMissingProgressNote || (appointmentId && rejectedAppointmentIds.has(appointmentId))) {
      onProceedToConclude()
      return
    }

    const confirmed = await confirm({
      title: "patient.conclude.missingConsultationReport.title",
      content: "patient.conclude.missingConsultationReport.content",
      cancelText: "patient.conclude.missingConsultationReport.reject",
      confirmText: "patient.conclude.missingConsultationReport.createConsultationReport",
    })

    if (confirmed) {
      FaroInsightsWrapper.pushEvent("documents_consultation_report_start")
      setConsultationReportRef(null)
      openDrawer(
        () => <ConsultationReportDrawer progressNoteId={firstMissingProgressNote.id} />,
        "ConsultationReportDrawer",
      )
    } else {
      if (appointmentId) {
        rejectedAppointmentIds.add(appointmentId)
      }
      FaroInsightsWrapper.pushEvent("consultation_report_reminder_rejected")
      onProceedToConclude()
    }
  }

  return checkMissingConsultationReport
}

export { useMissingConsultationReportCheck }
